import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Sparkles, Clock, AlertTriangle, Crown, X } from "lucide-react";

const PLAN_LABELS = {
  trial: "Trial 14gg",
  starter: "Starter",
  business: "Business",
  enterprise: "Enterprise",
};

/**
 * Banner status piano per admin: mostra piano corrente, giorni rimasti, CTA upgrade.
 * Si nasconde da solo per superadmin (non rilevante) e per piani >30gg di scadenza.
 */
export default function PlanStatusBanner({ user }) {
  const nav = useNavigate();
  const [company, setCompany] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("tb_plan_banner_dismissed") === "1");

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
  }, [user?.role]);

  if (user?.role !== "admin" || !company || dismissed) return null;

  const plan = company.plan || user.plan || "trial";
  const expiresIso = company.plan_expires_at;
  const isTrial = plan === "trial";
  const cardValidated = !!company.card_validated;

  let daysLeft = null;
  if (expiresIso) {
    const diff = new Date(expiresIso).getTime() - Date.now();
    daysLeft = Math.max(0, Math.ceil(diff / 86400000));
  }

  // Non mostrare se piano paid e mancano > 30 giorni
  if (!isTrial && daysLeft !== null && daysLeft > 30) return null;
  // Non mostrare se piano = trial ma ancora 14gg pieni e card non validata (l'utente ha appena scelto trial)
  if (isTrial && !cardValidated && daysLeft === null) return null;

  // Stile in base ai giorni rimasti
  let tone = "info";
  if (daysLeft !== null) {
    if (daysLeft <= 3) tone = "danger";
    else if (daysLeft <= 7) tone = "warning";
    else tone = "info";
  }
  const styles = {
    info:    { bg: "from-[#FFF0E3] to-[#FFFAF5]", border: "border-[#FF7A29]/30", text: "text-[#0A0A0A]", icon: Sparkles, iconColor: "text-[#FF7A29]" },
    warning: { bg: "from-amber-50 to-orange-50", border: "border-amber-400", text: "text-amber-900", icon: Clock, iconColor: "text-amber-600" },
    danger:  { bg: "from-red-50 to-orange-50", border: "border-red-400", text: "text-red-900", icon: AlertTriangle, iconColor: "text-red-600" },
  }[tone];
  const Icon = styles.icon;

  const planLabel = PLAN_LABELS[plan] || plan;

  let title = "";
  let subtitle = "";
  let ctaLabel = "";
  if (isTrial) {
    if (daysLeft === null || daysLeft === 14) {
      title = "Il tuo periodo di prova è iniziato";
      subtitle = "Hai 14 giorni per provare tutte le funzionalità · scegli un piano per continuare senza interruzioni";
      ctaLabel = "Scegli un piano completo";
    } else if (daysLeft <= 0) {
      title = "Trial scaduto";
      subtitle = "Scegli un piano ora per riattivare l'accesso completo all'app";
      ctaLabel = "Attiva piano";
    } else {
      title = `${daysLeft} ${daysLeft === 1 ? "giorno rimanente" : "giorni rimanenti"} di trial`;
      subtitle = daysLeft <= 3
        ? "Il trial sta per scadere · scegli ora un piano per non perdere i dati"
        : "Sblocca tutto upgrading al piano completo · risparmia il 10% con il pagamento annuale";
      ctaLabel = daysLeft <= 7 ? "Effettua upgrade adesso" : "Scopri i piani";
    }
  } else {
    if (daysLeft === null) return null;
    if (daysLeft <= 0) {
      title = `Piano ${planLabel} scaduto`;
      subtitle = "Rinnova ora per evitare l'interruzione del servizio";
      ctaLabel = "Rinnova piano";
    } else {
      title = `Piano ${planLabel} · scade in ${daysLeft} ${daysLeft === 1 ? "giorno" : "giorni"}`;
      subtitle = "Rinnova in anticipo per evitare interruzioni · upgrade per più dipendenti e funzionalità avanzate";
      ctaLabel = daysLeft <= 7 ? "Rinnova ora" : "Gestisci piano";
    }
  }

  const goToPlans = () => nav("/onboarding/plan");
  const goToBilling = () => nav("/app/billing");

  const dismiss = () => {
    sessionStorage.setItem("tb_plan_banner_dismissed", "1");
    setDismissed(true);
  };

  return (
    <div className={`bg-gradient-to-r ${styles.bg} border-b ${styles.border} px-6 py-3 flex items-center gap-3 relative`} data-testid="plan-status-banner">
      <div className={`w-10 h-10 rounded-xl bg-white/60 flex items-center justify-center flex-shrink-0 ${tone === "danger" ? "animate-pulse" : ""}`}>
        <Icon className={`w-5 h-5 ${styles.iconColor}`}/>
      </div>
      <div className={`flex-1 min-w-0 ${styles.text}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm">{title}</span>
          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-white/70 ${styles.iconColor}`}>{planLabel}</span>
        </div>
        <div className="text-xs opacity-80 truncate">{subtitle}</div>
      </div>
      <Button onClick={isTrial || (daysLeft !== null && daysLeft <= 7) ? goToPlans : goToBilling} data-testid="plan-banner-cta" size="sm" className={`rounded-full text-xs h-9 px-4 flex-shrink-0 ${tone === "danger" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-[#FF7A29] hover:bg-[#E85D00] text-white"} btn-glow`}>
        <Crown className="w-3.5 h-3.5 mr-1"/>
        {ctaLabel}
      </Button>
      <button onClick={dismiss} data-testid="plan-banner-close" className="opacity-50 hover:opacity-100 transition flex-shrink-0" aria-label="chiudi">
        <X className="w-4 h-4"/>
      </button>
    </div>
  );
}
